import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { CircularProgress } from "@material-ui/core";
import NewCraftEditor from '../../components/EditorNuevo/pages';
import { useTitle } from "../../hooks/useTitle";

const PrescriptionTemplatesEditorPage = () => {
  const { templateId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [templateName, setTemplateName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(Boolean(templateId && templateId !== "new"));
  const [initialContent, setInitialContent] = useState(null);
  const [isDuplicable, setIsDuplicable] = useState(true);
  const [isGlobal, setIsGlobal] = useState(false); 
  const editorRef = useRef(null);
  
  const isSuperadmin = user && user.profile === "superadmin"
  const hasAccess = (user && user.isDoctor) || (user && user.profile === "asistente") || (isSuperadmin);
  const [isOwner, setIsOwner] = useState(true); 
  const isNew = !templateId || templateId === "new";
  
  let pageTitle = "Crear Nueva Plantilla";
  if (!isNew) {
    pageTitle = templateName ? "Editar: " + templateName : "Cargando Plantilla...";
  }
  
  useTitle(pageTitle);

  useEffect(() => {
    let isMounted = true;
    
    if (isNew) {
      setTemplateName("");
      setInitialContent(null);
      setIsDuplicable(true);
      setIsGlobal(false);
      setIsOwner(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setIsOwner(false);

    api.get("/prescriptionTemplates/" + templateId)
      .then(res => {
        if (!isMounted) return;
        const tpl = res.data;
        setTemplateName(tpl.name || "");
        setInitialContent(tpl.content || "");
        setIsDuplicable(tpl.duplicable);
        setIsGlobal(tpl.isGlobal);

        if (tpl.userId === user.id) {
          setIsOwner(true);
        } else {
          setIsOwner(false);
          toast.info("Estás viendo una plantilla que no te pertenece. No podrás guardarla.");
        }
      })
      .catch(err => {
        toastError(err);
        history.push("/PrescriptionTemplates");
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [templateId, user.id, history, isNew]);

  const handleSave = async () => {
    if (!templateName.trim()) return toast.error("El nombre de la plantilla es obligatorio.");

    const contentString = editorRef.current?.getEditorState();
    if (!contentString) {
      toast.error("No se pudo obtener el contenido del editor.");
      return;
    }

    setIsSaving(true);
    const payload = {
      name: templateName,
      content: contentString,
      variables: [],
      duplicable: isDuplicable,
      isGlobal: isSuperadmin ? isGlobal : false,
    };

    try {
      if (!isNew) {
        await api.put("/prescriptionTemplates/" + templateId, payload);
        toast.success("Plantilla actualizada con éxito.");

        setInitialContent(contentString); 
      } else {
        const { data: newTpl } = await api.post("/prescriptionTemplates", payload);
        toast.success("Plantilla creada con éxito.");
        history.push("/PrescriptionTemplates/" + newTpl.id);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <MainContainer>
        <MainHeader><Title>Plantillas de Recetas</Title></MainHeader>
        <p style={{ padding: 20 }}>No tienes acceso a esta sección.</p>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{pageTitle}</Title>
        <MainHeaderButtonsWrapper>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {loading ? <CircularProgress style={{ margin: 40 }} /> : 
        <NewCraftEditor 
          ref={editorRef} 
          initialContent={initialContent}
          templateName={templateName}
          isSaving={isSaving}
          isLoading={loading}
          isDuplicable={isDuplicable}
          isGlobal={isGlobal}
          isSuperadmin={isSuperadmin}
          isEditable={isOwner} 
          onNameChange={isOwner ? (newName) => setTemplateName(newName) : null}
          onSave={isOwner ? handleSave : null}
          onDuplicableChange={isOwner ? setIsDuplicable : null}
          onGlobalChange={isOwner ? setIsGlobal : null}
        />
      }
    </MainContainer>
  );
};

export default PrescriptionTemplatesEditorPage;